import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Specialty } from '../types';
import { ADDRESS, CONTACT_EMAIL, Footer, PageBand, V2, WHATSAPP_DISPLAY, WHATSAPP_URL, usePageTitle } from './shared';

/** Only things the system really does. Hours, address and contact come from the same constants as the footer. */
const FAQ: Array<{ q: string; a: React.ReactNode; text: string }> = [
  {
    q: '¿Cómo pido un turno?',
    text: 'Elegís la especialidad, el día y uno de los horarios disponibles, y nos dejás tu nombre, tu email y un teléfono. No hace falta crear una cuenta.',
    a: <>Elegís la <Link to="/#especialidades">especialidad</Link>, el día y uno de los horarios disponibles, y nos dejás tu nombre, tu email y un teléfono. No hace falta crear una cuenta.</>,
  },
  {
    q: '¿Cuándo queda confirmado mi turno?',
    text: 'Al reservar, el horario queda apartado para vos y te llega un email. El consultorio lo confirma, normalmente en menos de 24 horas hábiles, y te avisamos por email. Si no pudiéramos confirmarlo, también te avisamos y el horario se libera.',
    a: 'Al reservar, el horario queda apartado para vos y te llega un email. El consultorio lo confirma, normalmente en menos de 24 horas hábiles, y te avisamos por email. Si no pudiéramos confirmarlo, también te avisamos y el horario se libera.',
  },
  {
    q: '¿Con cuánta anticipación puedo reservar?',
    text: 'Hay que reservar con al menos 24 horas de anticipación. Para algo más urgente, escribinos por WhatsApp.',
    a: 'Hay que reservar con al menos 24 horas de anticipación. Para algo más urgente, escribinos por WhatsApp.',
  },
  {
    q: '¿Puedo cancelar o ver mi turno?',
    text: 'Sí. El email de tu reserva trae un enlace personal donde ves el estado de tu turno y podés cancelarlo hasta 12 horas antes. Pasado ese plazo, avisanos por WhatsApp.',
    a: 'Sí. El email de tu reserva trae un enlace personal donde ves el estado de tu turno y podés cancelarlo hasta 12 horas antes. Pasado ese plazo, avisanos por WhatsApp.',
  },
  {
    q: '¿Cuánto cuesta la consulta y qué pasa si falto?',
    text: 'Los honorarios los define cada profesional. Consultalos por WhatsApp antes de reservar. Si necesitás faltar, avisá con tiempo: cada profesional acuerda con su paciente cómo se trata una sesión a la que se falta.',
    a: 'Los honorarios los define cada profesional. Consultalos por WhatsApp antes de reservar. Si necesitás faltar, avisá con tiempo: cada profesional acuerda con su paciente cómo se trata una sesión a la que se falta.',
  },
  {
    q: '¿Dónde están y qué días atienden?',
    text: `Estamos en ${ADDRESS.line}, ${ADDRESS.area}. Atendemos de lunes a sábados, con turno previo.`,
    a: <>Estamos en <a href={ADDRESS.map} target="_blank" rel="noreferrer">{ADDRESS.line}, {ADDRESS.area}</a>. Atendemos de lunes a sábados, con turno previo.</>,
  },
  {
    q: '¿Cómo los contacto?',
    text: `Por WhatsApp al ${WHATSAPP_DISPLAY} o por email a ${CONTACT_EMAIL}. Respondemos en menos de 24 horas hábiles.`,
    a: <>Por WhatsApp al <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">{WHATSAPP_DISPLAY}</a> o por email a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Respondemos en menos de 24 horas hábiles.</>,
  },
  {
    q: '¿Mis datos están seguros?',
    text: 'Usamos tus datos solo para gestionar tu turno y avisarte. Tu enlace personal es privado: no lo compartas.',
    a: 'Usamos tus datos solo para gestionar tu turno y avisarte. Tu enlace personal es privado: no lo compartas.',
  },
];

const FaqV2: React.FC = () => {
  usePageTitle('Preguntas frecuentes');
  const [specialties, setSpecialties] = useState<Specialty[] | null>(null);
  useEffect(() => { api.get('/public/specialties').then(r => setSpecialties(r.data)).catch(() => setSpecialties(null)); }, []);

  // structured data so search engines can show the answers
  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQ.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.text } })) });
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, []);

  return (
    <V2>
      <PageBand title="Preguntas frecuentes" subtitle="Lo que más nos preguntan antes de pedir un turno." />
      <main className="v2-section">
        <div className="wrap v2-faq">
          {FAQ.map(f => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
          <p className="v2-faq-more">¿No encontraste lo que buscabas? Escribinos por <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">WhatsApp</a> o mirá cómo <Link to="/#contacto">llegar y contactarnos</Link>.</p>
        </div>
      </main>
      <Footer specialties={specialties} />
    </V2>
  );
};

export default FaqV2;
